import { useCallback, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Spinner,
  Text,
  Tooltip,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  ArrowSyncFilled,
  CheckmarkCircleFilled,
  CopyRegular,
  PlayRegular,
  QuestionCircleFilled,
  StopRegular,
  SubtractCircleFilled,
} from '@fluentui/react-icons';
import {
  useApplicationStatus,
  useStartApplication,
  useStopApplication,
} from '@/hooks/useApplicationManagement';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useCopyWithToast } from '@/hooks/useCopyWithToast';
import { useGraphPrincipals, type GraphPrincipal } from '@/hooks/useGraphPrincipals';
import { useTelemetry } from '@/hooks/useTelemetry';
import { formatDateTime } from '@/utils/dateTime';
import { GUID_PATTERN } from '@/utils/primitives';
import type { AutomationApp, AutomationIdentity, RunningStatus } from '@/types/automation';
import { useApplicationStatusTabStyles } from './ApplicationStatusTab.styles';

export interface ApplicationStatusTabProps {
  projectName: string;
  appName: string;
}

function normalizeRunningStatus(status: RunningStatus | null | undefined): RunningStatus {
  switch (status) {
    case 'Running':
    case 'Stopped':
    case 'Starting':
    case 'Stopping':
    case 'Unknown':
      return status;
    default:
      return 'Unknown';
  }
}

function getErrorMessage(error: Error | unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Unable to load application status.';
}

const IDENTITY_TYPE_LABELS: Record<string, string> = {
  None: 'None',
  SystemAssigned: 'System-assigned',
  UserAssigned: 'User-assigned',
  'SystemAssigned,UserAssigned': 'System-assigned + User-assigned',
};

function formatIdentityTypeLabel(type: string | undefined): string {
  if (!type) return 'None';
  // ARM returns the combined value as either "SystemAssigned,UserAssigned" or
  // "SystemAssigned, UserAssigned" (with a space) depending on the RP / spec version. Strip
  // whitespace before lookup so both render the same friendly label.
  const normalized = type.replace(/\s+/g, '');
  return IDENTITY_TYPE_LABELS[normalized] ?? type;
}

const PRINCIPAL_TYPE_TO_BADGE_LABEL: Record<GraphPrincipal['type'], string> = {
  user: 'User',
  group: 'Group',
  application: 'Application',
  servicePrincipal: 'Service principal',
  unknown: 'Service identity',
};

/**
 * Map runtime status to a Fluent Badge color. Using Fluent's built-in palette keeps the
 * pill aligned with the rest of the design system (RuntimeVersionTab, EntityOverviewHeader,
 * etc.) and inherits forced-colors / high-contrast support without per-state CSS overrides.
 */
const STATUS_BADGE_COLOR: Record<RunningStatus, 'success' | 'warning' | 'subtle'> = {
  Running: 'success',
  Starting: 'warning',
  Stopping: 'warning',
  Stopped: 'subtle',
  Unknown: 'subtle',
};

const SYNC_ICON = <ArrowSyncFilled />;

/**
 * Status icon shown inside the badge. Filled icons read more clearly at small
 * sizes than the bare text pill and reinforce the color signal for users who
 * can't rely on color alone.
 */
const STATUS_BADGE_ICON: Record<RunningStatus, JSX.Element> = {
  Running: <CheckmarkCircleFilled />,
  Starting: SYNC_ICON,
  Stopping: SYNC_ICON,
  Stopped: <SubtractCircleFilled />,
  Unknown: <QuestionCircleFilled />,
};

/**
 * Map ARM provisioningState to a Fluent Badge color. Provisioning state is the
 * ARM resource-lifecycle signal (was the create/update operation successful?)
 * and reads alongside the runtime status badge in the header.
 */
const getProvisioningBadgeColor = (
  state: string | undefined,
): 'success' | 'danger' | 'warning' | 'informative' | 'subtle' => {
  if (!state) return 'subtle';
  const normalized = state.toLowerCase();
  if (normalized === 'succeeded') return 'success';
  if (normalized === 'failed') return 'danger';
  if (normalized === 'canceled' || normalized === 'cancelled') return 'warning';
  // Creating / Updating / Deleting / Accepted / Running etc. — in-flight ARM ops
  return 'informative';
};

interface ResolvedPrincipalProps {
  /** The raw value from properties.createdBy / properties.updatedBy. Usually a GUID. */
  rawValue: string | null | undefined;
  /** Result of useGraphPrincipals - a map keyed by lowercased GUID. May be undefined while loading. */
  principals: Record<string, GraphPrincipal> | undefined;
}

/**
 * Resolves a raw properties.createdBy / properties.updatedBy GUID to a display-friendly
 * principal. Empty values render as an em-dash. Non-GUID legacy values pass through. While
 * Graph is loading, the raw GUID renders to avoid layout shift. On success the displayName
 * (with email as accessible description for users) replaces it.
 */
const ResolvedPrincipal = ({ rawValue, principals }: ResolvedPrincipalProps) => {
  // NOTE: don't use isDisplayableValue here  it rejects bare GUIDs, but bare GUIDs are
  // exactly the input we expect from properties.createdBy / updatedBy.
  if (!rawValue || rawValue.trim().length === 0) {
    return <span>{'\u2014'}</span>;
  }
  const trimmed = rawValue.trim();
  const isGuid = GUID_PATTERN.test(trimmed);

  // Legacy / local values that are not AAD object IDs: render as-is, no lookup possible.
  if (!isGuid) {
    return <span>{trimmed}</span>;
  }

  const resolved = principals?.[trimmed.toLowerCase()];

  if (!resolved) {
    // Pre-resolution OR resolution failed: show the GUID. We do not have a useful name yet.
    return <span>{trimmed}</span>;
  }

  const visibleName = resolved.displayName || resolved.userPrincipalName || resolved.email || trimmed;
  const isUser = resolved.type === 'user';
  const supplemental = resolved.userPrincipalName || resolved.email;

  if (isUser) {
    if (supplemental && supplemental !== visibleName) {
      return (
        <Tooltip content={supplemental} relationship="description">
          <span>{visibleName}</span>
        </Tooltip>
      );
    }
    return <span>{visibleName}</span>;
  }

  const badgeLabel = PRINCIPAL_TYPE_TO_BADGE_LABEL[resolved.type] ?? PRINCIPAL_TYPE_TO_BADGE_LABEL.unknown;
  return (
    <span>
      <Badge appearance="ghost" color="subtle" aria-label={badgeLabel + " principal"}>
        {badgeLabel}
      </Badge>
      {' '}
      <span>{visibleName}</span>
    </span>
  );
};

/**
 * Read a GUID through the same useGraphPrincipals map and report whether a real
 * displayName was resolved. Used by the Managed identity row's Persona, which needs
 * to disable the "Copy name" affordance when we'd otherwise be copying the raw GUID.
 */
function readResolvedPrincipal(
  rawValue: string | null | undefined,
  principals: Record<string, GraphPrincipal> | undefined,
): { displayName: string; isResolved: boolean } {
  if (!rawValue || rawValue.trim().length === 0) return { displayName: '', isResolved: false };
  const trimmed = rawValue.trim();
  if (!GUID_PATTERN.test(trimmed)) return { displayName: trimmed, isResolved: true };
  const resolved = principals?.[trimmed.toLowerCase()];
  if (!resolved) return { displayName: trimmed, isResolved: false };
  const name = resolved.displayName || resolved.userPrincipalName || resolved.email;
  return name ? { displayName: name, isResolved: true } : { displayName: trimmed, isResolved: false };
}

interface CopyableMonoValueProps {
  value: string;
  ariaLabel: string;
  onCopy?: () => void;
  monoClassName: string;
  rowClassName: string;
  buttonClassName: string;
}

const CopyableMonoValue = ({ value, ariaLabel, onCopy, monoClassName, rowClassName, buttonClassName }: CopyableMonoValueProps) => {
  const copy = useCopyWithToast();
  return (
    <span className={rowClassName}>
      <span className={monoClassName}>{value}</span>
      <Button
        appearance="subtle"
        size="small"
        icon={<CopyRegular />}
        aria-label={ariaLabel}
        className={buttonClassName}
        onClick={() => {
          onCopy?.();
          void copy(value, ariaLabel);
        }}
      />
    </span>
  );
};

interface UserAssignedIdentitiesListProps {
  identities: NonNullable<AutomationIdentity['userAssignedIdentities']>;
  styles: ReturnType<typeof useApplicationStatusTabStyles>;
  onCopy: (field: 'principalId' | 'clientId' | 'resourceId') => void;
}

const UserAssignedIdentitiesList = ({ identities, styles, onCopy }: UserAssignedIdentitiesListProps) => {
  const entries = Object.entries(identities);
  if (entries.length === 0) return null;
  return (
    <ul className={styles.userAssignedList} aria-label="User-assigned identities">
      {entries.map(([resourceId, value]) => (
        <li key={resourceId} className={styles.userAssignedItem}>
          <div className={styles.userAssignedHeader}>
            <CopyableMonoValue
              value={resourceId}
              ariaLabel="Copy user-assigned identity resource id"
              onCopy={() => onCopy('resourceId')}
              monoClassName={styles.monoValue}
              rowClassName={styles.detailsValueRow}
              buttonClassName={styles.copyButton}
            />
          </div>
          {value?.principalId && (
            <div className={styles.userAssignedField}>
              <span className={styles.userAssignedFieldLabel}>Principal ID</span>
              <CopyableMonoValue
                value={value.principalId}
                ariaLabel="Copy user-assigned principal ID"
                onCopy={() => onCopy('principalId')}
                monoClassName={styles.monoValue}
                rowClassName={styles.detailsValueRow}
                buttonClassName={styles.copyButton}
              />
            </div>
          )}
          {value?.clientId && (
            <div className={styles.userAssignedField}>
              <span className={styles.userAssignedFieldLabel}>Client ID</span>
              <CopyableMonoValue
                value={value.clientId}
                ariaLabel="Copy user-assigned client ID"
                onCopy={() => onCopy('clientId')}
                monoClassName={styles.monoValue}
                rowClassName={styles.detailsValueRow}
                buttonClassName={styles.copyButton}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};


interface ManagedIdentityRowProps {
  identity: AutomationIdentity | undefined;
  identityType: string;
  principalsData: Record<string, GraphPrincipal> | undefined;
  styles: ReturnType<typeof useApplicationStatusTabStyles>;
  /** Telemetry hook. `field` is `'name' | 'principalId' | 'tenantId'`. */
  onCopy: (field: 'name' | 'principalId' | 'tenantId') => void;
}

/**
 * Renders the Managed identity value cell. Three branches:
 *
 *  - `None` / missing       → "None" badge
 *  - SystemAssigned, name   → resolved display name + subtext + Copy button
 *  - SystemAssigned, no name → friendly type badge (avoids surfacing a bare GUID as a "name")
 *  - other (UserAssigned)   → friendly type badge
 *
 * Object ID / Tenant ID rows (when present) are appended underneath the
 * SystemAssigned header so the caller's grid cell stays flat.
 */
const ManagedIdentityRow = ({ identity, identityType, principalsData, styles, onCopy }: ManagedIdentityRowProps) => {
  const copyToClipboard = useCopyWithToast();
  const resolved = identity?.principalId
    ? readResolvedPrincipal(identity.principalId, principalsData)
    : null;
  const resolvedDisplayName = resolved?.displayName ?? '';

  const handleCopyName = useCallback(() => {
    onCopy('name');
    void copyToClipboard(resolvedDisplayName, 'Copy identity name');
  }, [onCopy, copyToClipboard, resolvedDisplayName]);
  const handleCopyPrincipalId = useCallback(() => onCopy('principalId'), [onCopy]);
  const handleCopyTenantId = useCallback(() => onCopy('tenantId'), [onCopy]);

  if (!identity?.type || identity.type === 'None') {
    return <Badge appearance="ghost" aria-label="Managed identity: None">None</Badge>;
  }

  const isSystemAssigned = identity.type.includes('SystemAssigned');
  if (!isSystemAssigned) {
    return (
      <Badge appearance="ghost" aria-label={`Managed identity: ${identityType}`}>
        {identityType}
      </Badge>
    );
  }

  return (
    <div className={styles.identityStack}>
      {resolved?.isResolved ? (
        <div className={styles.identityHeader}>
          <div className={styles.identityNameStack}>
            <span className={styles.metadataValue}>{resolved.displayName}</span>
            <span className={styles.identitySubtext}>System-assigned managed identity</span>
          </div>
          <Button
            appearance="subtle"
            size="small"
            icon={<CopyRegular />}
            aria-label="Copy identity name"
            className={styles.copyButton}
            onClick={handleCopyName}
          />
        </div>
      ) : (
        <Badge appearance="ghost" aria-label={`Managed identity: ${identityType}`}>
          {identityType}
        </Badge>
      )}
      {identity.principalId && (
        <div className={styles.identityIdRow}>
          <span className={styles.identityIdLabel}>Object ID</span>
          <CopyableMonoValue
            value={identity.principalId}
            ariaLabel="Copy system-assigned object ID"
            onCopy={handleCopyPrincipalId}
            monoClassName={styles.monoValue}
            rowClassName={styles.detailsValueRow}
            buttonClassName={styles.copyButton}
          />
        </div>
      )}
      {identity.tenantId && (
        <div className={styles.identityIdRow}>
          <span className={styles.identityIdLabel}>Tenant ID</span>
          <CopyableMonoValue
            value={identity.tenantId}
            ariaLabel="Copy system-assigned tenant ID"
            onCopy={handleCopyTenantId}
            monoClassName={styles.monoValue}
            rowClassName={styles.detailsValueRow}
            buttonClassName={styles.copyButton}
          />
        </div>
      )}
    </div>
  );
};


interface ResourceDetailsSectionProps {
  app: AutomationApp;
  projectName: string;
  appName: string;
  styles: ReturnType<typeof useApplicationStatusTabStyles>;
  trackEvent: ReturnType<typeof useTelemetry>['trackEvent'];
}

/**
 * "Resource details" — the enriched ARM-standard block surfaced from the single-GET
 * response. Rendered as a section inside the parent status card (separated by a
 * `borderTop` divider). Read-only; copy/open are the only interactions.
 */
const ResourceDetailsSection = ({ app, projectName, appName, styles, trackEvent }: ResourceDetailsSectionProps) => {
  const identity = app.identity;
  const systemData = app.systemData;

  const trackCopy = useCallback(
    (field: string) => {
      trackEvent('settings.app.details.copy', { projectName, appName, field });
    },
    [trackEvent, projectName, appName],
  );

  // Per Otto API: properties.createdBy and properties.updatedBy are raw AAD object IDs (GUIDs)
  // for apps. We batch-resolve both through Graph to render display names; systemData.createdBy is
  // an email/UPN string for projects but NOT for apps, so it isn't suitable here.
  const createdByGuid = app.properties?.createdBy;
  const lastModifiedByGuid = app.properties?.updatedBy;
  // The system-assigned identity's principal id is an AAD service principal id; include it in
  // the same Graph batch so the Managed identity row can show a display name beside the GUID.
  const systemIdentityPrincipalId = app.identity?.principalId;
  const principalsQuery = useGraphPrincipals([createdByGuid, lastModifiedByGuid, systemIdentityPrincipalId]);

  const identityType = formatIdentityTypeLabel(identity?.type);
  const isUserAssigned = !!identity?.type?.includes('UserAssigned');
  const userAssignedIdentities = identity?.userAssignedIdentities;

  return (
    <div className={styles.detailsSection}>
      <Text as="h3" className={styles.metadataTitle}>Resource details</Text>

      <dl className={styles.metadataGrid}>
        <div className={styles.metadataItem}>
          <dt className={styles.metadataLabel}>Created by</dt>
          <dd className={styles.metadataValue}>
            <ResolvedPrincipal rawValue={createdByGuid} principals={principalsQuery.data} />
          </dd>
        </div>

        <div className={styles.metadataItem}>
          <dt className={styles.metadataLabel}>Created</dt>
          <dd className={styles.metadataValue}>{systemData?.createdAt ? formatDateTime(systemData.createdAt, 'full') : '\u2014'}</dd>
        </div>

        <div className={styles.metadataItem}>
          <dt className={styles.metadataLabel}>Last modified by</dt>
          <dd className={styles.metadataValue}>
            <ResolvedPrincipal rawValue={lastModifiedByGuid} principals={principalsQuery.data} />
          </dd>
        </div>

        <div className={styles.metadataItem}>
          <dt className={styles.metadataLabel}>Last modified</dt>
          <dd className={styles.metadataValue}>{systemData?.lastModifiedAt ? formatDateTime(systemData.lastModifiedAt, 'full') : '\u2014'}</dd>
        </div>

        <div className={mergeClasses(styles.metadataItem, styles.metadataItemWide)}>
          <dt className={styles.metadataLabel}>Managed identity</dt>
          <dd className={styles.metadataValue}>
            <ManagedIdentityRow
              identity={identity}
              identityType={identityType}
              principalsData={principalsQuery.data}
              styles={styles}
              onCopy={(field) => trackCopy(`identity.${field}`)}
            />
          </dd>
        </div>
      </dl>

        {isUserAssigned && userAssignedIdentities && Object.keys(userAssignedIdentities).length > 0 && (
          <Accordion
            collapsible
            onToggle={(_e, data) => {
              if (data.openItems.includes('userAssigned')) {
                trackEvent('settings.app.identity.expand', { projectName, appName });
              }
            }}
          >
            <AccordionItem value="userAssigned">
              <AccordionHeader>User-assigned identities ({Object.keys(userAssignedIdentities).length})</AccordionHeader>
              <AccordionPanel>
                <UserAssignedIdentitiesList
                  identities={userAssignedIdentities}
                  styles={styles}
                  onCopy={(field) => trackCopy('identity.userAssigned.' + field)}
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
    </div>
  );
};

export const ApplicationStatusTab = ({ projectName, appName }: ApplicationStatusTabProps) => {
  const styles = useApplicationStatusTabStyles();
  const { trackEvent, trackException } = useTelemetry();
  const { canEdit, isLoading: isPermissionLoading } = useAppPermission();
  const { data, isLoading, isError, error } = useApplicationStatus(projectName, appName);
  const startMutation = useStartApplication(projectName, appName);
  const stopMutation = useStopApplication(projectName, appName);
  // Destructure the function refs we actually use; depending on these (rather than the whole
  // mutation object) keeps the useCallback identity stable across non-mutation re-renders.
  const { mutateAsync: startAsync } = startMutation;
  const { mutateAsync: stopAsync } = stopMutation;

  const [actionError, setActionError] = useState<string | null>(null);

  const runningStatus = normalizeRunningStatus(data?.properties?.runningStatus);
  const startBusy = startMutation.isPending || runningStatus === 'Starting';
  const stopBusy = stopMutation.isPending || runningStatus === 'Stopping';
  const actionsDisabled = startBusy || stopBusy;

  const handleStart = useCallback(async () => {
    setActionError(null);
    trackEvent('settings.app.start', {
      projectName,
      appName,
      runningStatus,
    });

    try {
      await startAsync();
    } catch (startError) {
      setActionError(startError instanceof Error ? startError.message : 'Failed to start the application.');
      trackException(startError, {
        action: 'start',
        projectName,
        appName,
      });
    }
  }, [appName, projectName, runningStatus, startAsync, trackEvent, trackException]);

  const handleStop = useCallback(async () => {
    setActionError(null);
    trackEvent('settings.app.stop', {
      projectName,
      appName,
      runningStatus,
    });

    try {
      await stopAsync();
    } catch (stopError) {
      setActionError(stopError instanceof Error ? stopError.message : 'Failed to stop the application.');
      trackException(stopError, {
        action: 'stop',
        projectName,
        appName,
      });
    }
  }, [appName, projectName, runningStatus, stopAsync, trackEvent, trackException]);

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingState} role="status" aria-label="Loading application status">
          <Skeleton className={styles.statusCard}>
            <div className={styles.loadingStack}>
              <div className={styles.skeletonRow}>
                <div className={styles.skeletonStatusInfo}>
                  <SkeletonItem size={28} />
                </div>
                <div className={styles.skeletonActions}>
                  <SkeletonItem size={32} />
                </div>
              </div>
              <div className={styles.skeletonGrid}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className={styles.metadataItem}>
                    <SkeletonItem size={12} />
                    <SkeletonItem size={16} />
                  </div>
                ))}
              </div>
            </div>
          </Skeleton>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.root}>
        <div className={styles.errorState}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Failed to load application status</MessageBarTitle>
              {getErrorMessage(error)}
            </MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  const renderActionButton = () => {
    if (isPermissionLoading || !canEdit) {
      return null;
    }

    if (runningStatus === 'Running' || runningStatus === 'Stopping') {
      return (
        <Button
          appearance="primary"
          icon={stopBusy ? <Spinner size="tiny" aria-hidden="true" /> : <StopRegular />}
          onClick={handleStop}
          disabled={actionsDisabled}
        >
          Stop
        </Button>
      );
    }

    if (runningStatus === 'Unknown') {
      return null;
    }

    return (
      <Button
        appearance="primary"
        icon={startBusy ? <Spinner size="tiny" aria-hidden="true" /> : <PlayRegular />}
        onClick={handleStart}
        disabled={actionsDisabled}
      >
        Start
      </Button>
    );
  };

  return (
    <div className={styles.root}>
      <div role="alert" aria-live="assertive">
        {actionError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Action failed</MessageBarTitle>
              {actionError}
            </MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" onClick={() => setActionError(null)}>
                Dismiss
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}
      </div>
      <div className={styles.statusCard}>
        <div className={styles.statusRow}>
          <div className={styles.statusBadges} aria-live="polite" aria-atomic="true">
            <Badge
              appearance="tint"
              color={STATUS_BADGE_COLOR[runningStatus]}
              size="large"
              shape="rounded"
              icon={STATUS_BADGE_ICON[runningStatus]}
              aria-label={`${runningStatus}, application status`}
            >
              {runningStatus}
            </Badge>
            {data?.properties?.provisioningState ? (
              <Badge
                appearance="tint"
                color={getProvisioningBadgeColor(data.properties.provisioningState)}
                size="large"
                shape="rounded"
                aria-label={`Provisioning: ${data.properties.provisioningState}`}
              >
                Provisioning: {data.properties.provisioningState}
              </Badge>
            ) : null}
          </div>
          <div className={styles.actionButtons}>{renderActionButton()}</div>
        </div>

        {data && (
          <ResourceDetailsSection
            app={data}
            projectName={projectName}
            appName={appName}
            styles={styles}
            trackEvent={trackEvent}
          />
        )}
      </div>
    </div>
  );
};

ApplicationStatusTab.displayName = 'ApplicationStatusTab';